import classNames from 'classnames';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLElement>, 'size' | 'type' | 'onChange' | 'onBlur'> {
  type?: string;
  size?: number | string;
  bsSize?: string;
  valid?: boolean;
  invalid?: boolean;
  tag?: React.ElementType;
  innerRef?: React.Ref<HTMLElement> | ((node: HTMLElement | null) => void);
  plaintext?: boolean;
  addon?: boolean;
  onChange?: (event: React.ChangeEvent<any>) => void;
  onBlur?: (event: React.FocusEvent<any>) => void;
}

const Input = forwardRef<HTMLElement, InputProps>((props, ref) => {
  const {
    className,
    type = 'text',
    bsSize,
    valid,
    invalid,
    tag,
    addon,
    plaintext,
    innerRef,
    size,
    ...attributes
  } = props;

  const internalRef = useRef<HTMLElement | null>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLElement, []);

  const setRef = (node: HTMLElement | null) => {
    internalRef.current = node;
    if (typeof innerRef === 'function') {
      innerRef(node);
    } else if (innerRef && typeof innerRef === 'object') {
      (innerRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  const checkInput = ['radio', 'checkbox'].indexOf(type) > -1;
  const isNotaNumber = /\D/g;

  const fileInput = type === 'file';
  const textareaInput = type === 'textarea';
  const selectInput = type === 'select';
  const rangeInput = type === 'range';
  let Tag: React.ElementType = tag || (selectInput || textareaInput ? type : 'input');

  let formControlClass: string | null = 'form-control';

  if (plaintext) {
    formControlClass = `${formControlClass}-plaintext`;
    Tag = tag || 'input';
  } else if (fileInput) {
    formControlClass = `${formControlClass}-file`;
  } else if (rangeInput) {
    formControlClass = `${formControlClass}-range`;
  } else if (checkInput) {
    if (addon) {
      formControlClass = null;
    } else {
      formControlClass = 'form-check-input';
    }
  }

  let resolvedBsSize = bsSize;
  let sizeAttr: number | string | undefined = size;
  if (size !== undefined && isNotaNumber.test(String(size))) {
    resolvedBsSize = String(size);
    sizeAttr = undefined;
  }

  const classes = classNames(
    className,
    invalid && 'is-invalid',
    valid && 'is-valid',
    resolvedBsSize ? `form-control-${resolvedBsSize}` : false,
    formControlClass
  );

  const finalAttrs: Record<string, unknown> = { ...attributes };
  if (sizeAttr !== undefined) finalAttrs.size = sizeAttr;
  if (Tag === 'input' || (tag && typeof tag === 'function')) {
    finalAttrs.type = type;
  }

  if (
    finalAttrs.children &&
    !(plaintext || type === 'select' || typeof Tag !== 'string' || Tag === 'select')
  ) {
    delete finalAttrs.children;
  }

  return <Tag {...finalAttrs} ref={setRef} className={classes} aria-invalid={invalid} />;
});

Input.displayName = 'Input';

export default Input;
